import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook for managing request cancellation with AbortController
 * Automatically cancels in-flight requests on:
 * - Component unmount
 * - Route changes
 * - Manual cancellation
 */
export function useRequestCancellation() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const location = useLocation();
  const locationRef = useRef(location.pathname);

  // Cancel all requests when route changes
  useEffect(() => {
    if (locationRef.current !== location.pathname) {
      cancelRequests();
      locationRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Cancel all requests on unmount
  useEffect(() => {
    return () => {
      cancelRequests();
    };
  }, []);

  const cancelRequests = useCallback(() => {
    if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      abortControllerRef.current.abort('Route change or component unmount');
    }
  }, []);

  const getAbortController = useCallback(() => {
    // Create new controller if needed
    if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
      abortControllerRef.current = new AbortController();
    }
    return abortControllerRef.current;
  }, []);

  const getAbortSignal = useCallback(() => {
    return getAbortController().signal;
  }, [getAbortController]);

  const createAbortablePromise = useCallback(<T>(
    promiseFactory: (signal: AbortSignal) => Promise<T>
  ): Promise<T> => {
    const signal = getAbortSignal();
    return promiseFactory(signal);
  }, [getAbortSignal]);

  return {
    cancelRequests,
    getAbortController,
    getAbortSignal,
    createAbortablePromise
  };
}

/**
 * Enhanced version for query integration
 */
export function useQueryCancellation() {
  const cancellation = useRequestCancellation();

  const createAbortableQueryFn = useCallback(<T>(
    queryFn: (signal?: AbortSignal) => Promise<T>
  ) => {
    return () => {
      const signal = cancellation.getAbortSignal();
      return queryFn(signal);
    };
  }, [cancellation]);

  return {
    ...cancellation,
    createAbortableQueryFn
  };
}